import { h, replaceContent } from '../lib/dom.js';
import { money, pct } from '../lib/format.js';
import { TIER1_DEFAULTS, TIER2_DEFAULTS, subdivisionFee } from '../../shared/defaults.js';
import { getDefaultOverrides, saveDefaultOverrides } from '../lib/storage.js';

export function LibraryPage() {
  let overrides = getDefaultOverrides();
  const content = h('div', { class: 'divide-y divide-[color:var(--color-rule)]' });

  const render = () => replaceContent(content, ...sections(overrides, onChange));
  const onChange = (patch) => {
    overrides = { ...overrides };
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) delete overrides[k];
      else overrides[k] = v;
    }
    saveDefaultOverrides(overrides);
    render();
  };
  render();

  return h('div', { class: 'max-w-[1200px] mx-auto px-6 md:px-10' },
    h('header', { class: 'pt-10 pb-6 border-b border-[color:var(--color-rule)] flex items-end justify-between gap-6 flex-wrap' },
      h('div', null,
        h('div', { class: 'folio' }, 'Library · Your defaults'),
        h('h1', { class: 'display display-md mt-1' }, 'Tune the numbers once, save them forever.'),
      ),
      h('p', { class: 'text-[color:var(--color-ink-soft)] max-w-[32ch] text-sm text-right' },
        'These defaults seed every new feasibility. Saved scenarios are unchanged.',
      ),
    ),
    content,
  );
}

function sections(overrides, onChange) {
  return [
    panel('Rates & GST',
      [
        pctRow('Stamp duty', 'stamp_duty_pct', TIER1_DEFAULTS.stamp_duty_pct, overrides, onChange),
        pctRow('Selling cost', 'selling_cost_pct', TIER1_DEFAULTS.selling_cost_pct, overrides, onChange),
        pctRow('Land loan rate', 'land_loan_rate', TIER1_DEFAULTS.land_loan_rate, overrides, onChange),
        pctRow('Development loan rate', 'dev_loan_rate', TIER1_DEFAULTS.dev_loan_rate, overrides, onChange),
        pctRow('Construction contingency', 'construction_contingency_pct', TIER1_DEFAULTS.construction_contingency_pct, overrides, onChange),
      ],
    ),
    panel('Build assumptions',
      [
        moneyRow('Demolition', 'demolition', TIER2_DEFAULTS.demolition, overrides, onChange),
        moneyRow('Design & approvals', 'design_approvals', TIER2_DEFAULTS.design_approvals, overrides, onChange),
        moneyRow('Finance fees', 'finance_fees', TIER2_DEFAULTS.finance_fees, overrides, onChange),
        moneyRow('Marketing', 'marketing', TIER2_DEFAULTS.marketing, overrides, onChange),
        moneyRow('Authorities', 'authorities', TIER2_DEFAULTS.authorities, overrides, onChange),
      ],
    ),
    panel('Subdivision fee schedule',
      [1, 2, 3, 4, 5, 6].map((n) =>
        h('div', { class: 'leader py-2' },
          h('span', null, '1 → ', h('span', { class: 'num' }, n), ' dwellings'),
          h('span', { class: 'dots' }),
          h('span', { class: 'num display display-sm' }, money(subdivisionFee(n))),
        ),
      ),
      'Stepped defaults baked into the calculator. Override per scenario from the feasibility form.',
    ),
  ];
}

function panel(title, children, note) {
  return h('section', { class: 'py-8 grid md:grid-cols-12 gap-8' },
    h('div', { class: 'md:col-span-4' },
      h('h2', { class: 'display display-sm' }, title),
      note ? h('p', { class: 'mt-3 text-sm text-[color:var(--color-ink-dim)]' }, note) : null,
    ),
    h('div', { class: 'md:col-span-8 space-y-1' }, ...children),
  );
}

function pctRow(label, key, defaultValue, overrides, onChange) {
  const active = overrides[key] ?? defaultValue;
  const isOverride = overrides[key] !== undefined;
  return h('label', { class: 'leader py-2 border-b border-[color:var(--color-rule-soft)]' },
    h('span', { class: 'flex items-baseline gap-3 min-w-0' },
      h('span', null, label),
      isOverride ? h('button', {
        type: 'button',
        class: 'text-[0.7rem] folio text-[color:var(--color-ink-faint)] hover:text-[color:var(--color-ink)]',
        onclick: (e) => { e.preventDefault(); onChange({ [key]: undefined }); },
      }, 'reset') : null,
    ),
    h('span', { class: 'dots' }),
    h('span', { class: 'flex items-baseline gap-2 shrink-0' },
      h('span', { class: 'text-[0.72rem] text-[color:var(--color-ink-faint)]' }, `was ${pct(defaultValue)}`),
      h('input', {
        type: 'number',
        class: 'field w-20 text-right py-1',
        step: 0.01,
        min: 0,
        value: +(active * 100).toFixed(4),
        oninput: (e) => {
          const v = e.target.value === '' ? undefined : Number(e.target.value) / 100;
          onChange({ [key]: Number.isFinite(v) ? v : undefined });
        },
      }),
      h('span', { class: 'text-[color:var(--color-ink-faint)]' }, '%'),
    ),
  );
}

function moneyRow(label, key, defaultValue, overrides, onChange) {
  const active = overrides[key] ?? defaultValue;
  const isOverride = overrides[key] !== undefined;
  return h('label', { class: 'leader py-2 border-b border-[color:var(--color-rule-soft)]' },
    h('span', { class: 'flex items-baseline gap-3 min-w-0' },
      h('span', null, label),
      isOverride ? h('button', {
        type: 'button',
        class: 'text-[0.7rem] folio text-[color:var(--color-ink-faint)] hover:text-[color:var(--color-ink)]',
        onclick: (e) => { e.preventDefault(); onChange({ [key]: undefined }); },
      }, 'reset') : null,
    ),
    h('span', { class: 'dots' }),
    h('span', { class: 'flex items-baseline gap-2 shrink-0' },
      h('span', { class: 'text-[0.72rem] text-[color:var(--color-ink-faint)]' }, `was ${money(defaultValue)}`),
      h('span', { class: 'text-[color:var(--color-ink-faint)]' }, '$'),
      h('input', {
        type: 'number',
        class: 'field w-28 text-right py-1',
        step: 100,
        min: 0,
        value: active,
        oninput: (e) => {
          const v = e.target.value === '' ? undefined : Number(e.target.value);
          onChange({ [key]: Number.isFinite(v) ? v : undefined });
        },
      }),
    ),
  );
}
